import store from "@/stores/store";
import utils from "@/utils";
import {
    entityHasBundlesFeature,
    entityHasFeature,
} from "@/utils/features";
import { userHasAnyPermission } from "@/utils/groupPermissions";

/**
 * Scope checks for sidebar / menu entries, keyed by Vue route name.
 *
 * Menu visibility follows metapermission scopes so incidental permissions from
 * another scope do not reveal navigation items.
 */
const MENU_SCOPE_CHECKS = {
    cart: () => utils.userHasPermission("order.add_providerorder"),
    "ap-configuration": () => ["ap.configure_appolicy", "ap.view_apreview", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    bankaccount: () => utils.userHasPermission("billing.view_billingaccount"),
    bankaccounts: () => utils.userHasPermission("billing.view_billingaccount"),
    finance: () => utils.userHasPermission("billing.view_customerinvoice") || utils.userHasPermission("billing.view_providerinvoice"),
    "finance-payments": () => utils.userHasPermission("billing.view_customerinvoice") || utils.userHasPermission("billing.view_providerinvoice"),
    "finance-aged": () => utils.userHasPermission("billing.view_customerinvoice") || utils.userHasPermission("billing.view_providerinvoice"),
    "accounting-ledger": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    "accounting-chart": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    "accounting-posting": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    "accounting-settings": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    tasks: () => utils.userHasDomainPermission("work.view_worktask"),
    task: () => utils.userHasDomainPermission("work.view_worktask"),
    calendar: () => utils.userHasDomainPermission("agenda.view_calendarevent"),
    projects: () => utils.userHasDomainPermission("work.view_workproject"),
    project: () => utils.userHasDomainPermission("work.view_workproject"),
    knowledge: () => utils.userHasDomainPermission("knowledge.view_knowledgesection"),
    "knowledge-section": () => utils.userHasDomainPermission("knowledge.view_knowledgesection"),

    dashboard: () => true,
    search: () => true,
    invitations: () => utils.userHasScope("read_invitations", "write_invitations"),
    customerorders: () =>
        utils.userHasScope(
            "read_customerorders",
            "write_customerorders",
            "assign_customerorders",
        ),
    providerorders: () =>
        utils.userHasScope(
            "read_providerorders",
            "write_providerorders",
            "validate_providerorders",
        ),
    "providerorders-toapprove": () =>
        utils.userHasScope("validate_providerorders"),
    customerinvoices: () =>
        utils.userHasScope("read_customerinvoices", "write_customerinvoices", "paid_customerinvoices", "record_customerpayments", "allocate_customerpayments", "correct_customerpayments"),
    providerinvoices: () =>
        utils.userHasScope(
            "read_providerinvoices",
            "write_providerinvoices",
            "validate_providerinvoices",
            "paid_providerinvoices",
            "record_providerpayments",
            "allocate_providerpayments",
            "correct_providerpayments",
        ),
    catalogs: () => utils.userHasScope("read_sellcatalogs", "write_sellcatalogs"),
    bundles: () =>
        entityHasBundlesFeature(store) &&
        utils.userHasScope("read_bundles", "write_bundles"),
    production: () =>
        entityHasBundlesFeature(store) &&
        utils.userHasScope(
            "read_productionrequests",
            "write_productionrequests",
            "confirm_productionrequests",
        ),
    customers: () => utils.userHasScope("read_customers", "write_customers"),
    products: () => utils.userHasScope("read_products", "write_products"),
    stock: () => utils.userHasScope("read_stocks", "write_stocks"),
    shipments: () =>
        utils.userHasScope("read_shipments", "write_shipments", "process_shipments", "cancel_shipments"),
    receipts: () =>
        utils.userHasScope("read_receipts", "write_receipts", "process_receipts"),
    movements: () => utils.userHasScope("read_stocks", "write_stocks"),
    operators: () => utils.userHasScope("read_operators", "write_operators"),
    providers: () => utils.userHasScope("read_providers", "write_providers"),
    comparator: () =>
        utils.userHasScope("read_providerorders", "write_providerorders"),
    replenish: () =>
        utils.userHasScope("read_providerorders", "write_providerorders"),
    toorder: () =>
        utils.userHasScope("read_providerorders", "write_providerorders"),
    allocations: () => utils.userHasScope("read_allocations", "write_allocations"),
    commissions: () => utils.userHasScope("read_commissions", "write_commissions"),
    salesinsights: () => utils.userHasScope("read_salesinsights"),
    agents: () => utils.userHasScope("read_agents", "write_agents"),
    radar: () => false,
    deliverysites: () =>
        utils.userHasScope("read_administration", "write_administration"),
    establishments: () =>
        entityHasFeature(store, "establishments") &&
        utils.userHasScope("read_establishments", "write_establishments"),
    stocks: () =>
        utils.userHasScope("read_administration", "write_administration"),
    billingaccounts: () =>
        utils.userHasScope("read_administration", "write_administration"),
    producttypes: () =>
        utils.userHasScope("read_administration", "write_administration"),
    campaigns: () =>
        utils.userHasScope(
            "read_communications",
            "write_communications",
            "send_communications",
        ),

    // Detail pages — same scopes as their list/menu counterparts
    campaign: () =>
        utils.userHasScope(
            "read_communications",
            "write_communications",
            "send_communications",
        ),
    customerorder: () =>
        utils.userHasScope(
            "read_customerorders",
            "write_customerorders",
            "assign_customerorders",
        ),
    providerorder: () =>
        utils.userHasScope(
            "read_providerorders",
            "write_providerorders",
            "validate_providerorders",
        ),
    customerinvoice: () =>
        utils.userHasScope("read_customerinvoices", "write_customerinvoices", "paid_customerinvoices", "record_customerpayments", "allocate_customerpayments", "correct_customerpayments"),
    providerinvoice: () =>
        utils.userHasScope(
            "read_providerinvoices",
            "write_providerinvoices",
            "validate_providerinvoices",
            "paid_providerinvoices",
            "record_providerpayments",
            "allocate_providerpayments",
            "correct_providerpayments",
        ),
    product: () => utils.userHasScope("read_products", "write_products"),
    providerproduct: () =>
        utils.userHasScope(
            "read_providers",
            "write_providers",
            "read_products",
            "write_products",
        ),
    customer: () => utils.userHasScope("read_customers", "write_customers"),
    provider: () => utils.userHasScope("read_providers", "write_providers"),
    shipment: () =>
        utils.userHasScope("read_shipments", "write_shipments", "process_shipments", "cancel_shipments"),
    receipt: () =>
        utils.userHasScope("read_receipts", "write_receipts", "process_receipts"),
    catalog: () => utils.userHasScope("read_sellcatalogs", "write_sellcatalogs"),
    assignedcatalog: () =>
        utils.userHasScope("read_sellcatalogs", "write_sellcatalogs"),
    bundle: () =>
        entityHasBundlesFeature(store) &&
        utils.userHasScope("read_bundles", "write_bundles"),
    establishment: () =>
        entityHasFeature(store, "establishments") &&
        utils.userHasScope("read_establishments", "write_establishments"),
    deliverysite: () =>
        utils.userHasScope("read_administration", "write_administration"),
    allocation: () => utils.userHasScope("read_allocations", "write_allocations"),
    contractline: () =>
        utils.userHasScope("read_customerinvoices", "write_customerinvoices", "paid_customerinvoices", "record_customerpayments", "allocate_customerpayments", "correct_customerpayments"),
    possale: () =>
        entityHasFeature(store, "pos_sync") &&
        (utils.userHasScope("read_possync", "write_possync", "process_possync") ||
            utils.userHasScope(
                "read_customerorders",
                "write_customerorders",
                "assign_customerorders",
            )),
    "settings-possync-list": () =>
        entityHasFeature(store, "pos_sync") &&
        utils.userHasScope("read_possync", "write_possync", "process_possync"),
    "settings-possync-detail": () =>
        entityHasFeature(store, "pos_sync") &&
        utils.userHasScope("read_possync", "write_possync", "process_possync"),
};

/**
 * Permission checks for route access (direct navigation / URL), keyed by route name.
 */
const ROUTE_PERMISSION_CHECKS = {
    cart: () => utils.userHasPermission("order.add_providerorder"),
    "ap-configuration": () => ["ap.configure_appolicy", "ap.view_apreview", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    bankaccount: () => utils.userHasPermission("billing.view_billingaccount"),
    bankaccounts: () => utils.userHasPermission("billing.view_billingaccount"),
    finance: () => utils.userHasPermission("billing.view_customerinvoice") || utils.userHasPermission("billing.view_providerinvoice"),
    "finance-payments": () => utils.userHasPermission("billing.view_customerinvoice") || utils.userHasPermission("billing.view_providerinvoice"),
    "finance-aged": () => utils.userHasPermission("billing.view_customerinvoice") || utils.userHasPermission("billing.view_providerinvoice"),
    "accounting-ledger": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    "accounting-chart": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    "accounting-posting": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    "accounting-settings": () => ["billing.view_billingaccount", "billing.view_customerinvoice", "billing.view_providerinvoice"].every(permission => utils.userHasPermission(permission)),
    tasks: () => utils.userHasDomainPermission("work.view_worktask"),
    task: () => utils.userHasDomainPermission("work.view_worktask"),
    calendar: () => utils.userHasDomainPermission("agenda.view_calendarevent"),
    projects: () => utils.userHasDomainPermission("work.view_workproject"),
    project: () => utils.userHasDomainPermission("work.view_workproject"),
    knowledge: () => utils.userHasDomainPermission("knowledge.view_knowledgesection"),
    "knowledge-section": () => utils.userHasDomainPermission("knowledge.view_knowledgesection"),

    dashboard: () => true,
    search: () => true,
    invitations: () => utils.userHasPermission("core.view_invitation"),
    customerorders: () => utils.userHasCustomerOrderPermission("view"),
    providerorders: () => utils.userHasProviderOrderPermission("view"),
    "providerorders-toapprove": () =>
        utils.userHasPermission("order.validate_providerorder"),
    customerinvoices: () => utils.userHasCustomerInvoicePermission("view"),
    providerinvoices: () => utils.userHasProviderInvoicePermission("view"),
    catalogs: () => utils.userHasSellCatalogPermission("view"),
    bundles: () =>
        entityHasBundlesFeature(store) &&
        utils.userHasPermission("catalog.view_bundle"),
    production: () =>
        entityHasBundlesFeature(store) &&
        utils.userHasPermission("catalog.view_productionrequest"),
    customers: () => {
        const profile = store.getters["session/current_entity_profile"];
        if (profile === "agent") {
            return utils.userHasPermission("core.view_entity");
        }
        return utils.userHasPermission("core.view_customer");
    },
    products: () => utils.userHasPermission("core.view_product"),
    stock: () => utils.userHasPermission("core.view_product"),
    shipments: () => utils.userHasShipmentPermission("view"),
    receipts: () => utils.userHasReceiptPermission("view"),
    movements: () => utils.userHasPermission("core.view_movement"),
    operators: () => utils.userHasPermission("core.view_operator"),
    providers: () => utils.userHasPermission("core.view_provider"),
    comparator: () => utils.userHasPermission("core.view_provider"),
    replenish: () => utils.userHasPermission("core.view_provider"),
    toorder: () => utils.userHasPermission("core.view_provider"),
    allocations: () => utils.userHasPermission("order.view_allocation"),
    commissions: () => utils.userHasCustomerOrderPermission("view"),
    salesinsights: () => utils.userHasPermission("order.view_sales_insights"),
    agents: () => {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            "catalog.view_catalogagentassignment",
            "core.view_customer",
        ]);
    },
    radar: () => utils.userHasPermission("core.view_customer"),
    deliverysites: () => utils.userHasPermission("core.view_deliverysite"),
    establishments: () => canAccessEstablishmentsRoute(),
    stocks: () => utils.userHasPermission("stock.view_stock"),
    billingaccounts: () => utils.userHasPermission("billing.view_billingaccount"),
    producttypes: () => utils.userHasPermission("core.view_producttype"),
    campaigns: () => utils.userHasPermission("campaigns.view_campaign"),
    campaign: () => utils.userHasPermission("campaigns.view_campaign"),
    establishment: () => canAccessEstablishmentsRoute(),
    possale: () =>
        entityHasFeature(store, "pos_sync") &&
        (utils.userHasPermission("connectors.view_pos_sync") ||
            utils.userHasCustomerOrderPermission("view")),
    "settings-possync-list": () =>
        entityHasFeature(store, "pos_sync") &&
        utils.userHasPermission("connectors.view_pos_sync"),
    "settings-possync-detail": () =>
        entityHasFeature(store, "pos_sync") &&
        utils.userHasPermission("connectors.view_pos_sync"),
};

/** Routes always visible regardless of onboarding menu_modules (matrix / tests). */
const ALWAYS_ALLOWED_MENU_ROUTES = new Set([
    "dashboard",
    "search",
    "producttypes",
    // Only listed in the sidebar when invitations exist, and gated by the
    // invitation scopes.
    "invitations",
    // Reached from the settings, so never filtered as a menu module.
    "deliverysites",
    "deliverysite",
    "settings-possync-list",
    "settings-possync-detail",
]);

/**
 * Map detail / helper route names to a parent key in `menu_modules` (from deriveCapabilities).
 * Used by the onboarding matrix / capability tests — not for live sidebar filtering.
 * List routes (customerorders, products, …) usually match module keys directly and need no entry here.
 */
const MENU_MODULE_ALIASES = {
    campaign: "campaigns",
    customerorder: "customerorders",
    possale: "establishments",
    providerorder: "providerorders",
    customerinvoice: "customerinvoices",
    providerinvoice: "providerinvoices",
    product: "products",
    providerproduct: "providers",
    customer: "customers",
    provider: "providers",
    shipment: "shipments",
    receipt: "receipts",
    catalog: "catalogs",
    assignedcatalog: "catalogs",
    bundle: "bundles",
    establishment: "establishments",
    allocation: "allocations",
    contractline: "customerinvoices",
    billingaccount: "billingaccounts",
    bankaccount: "bankaccounts",
    invitation: "invitations",
    inventory: "stock",
    "providerorders-toapprove": "providerorders",
    comparator: "providerorders",
    replenish: "providerorders",
    toorder: "providerorders",
    // salesinsights also allowed via commissions — see isMenuRouteAllowedByModules special case.
    salesinsights: "customerorders",
};

/**
 * Pure check: whether a route is allowed by onboarding-derived menu modules.
 * Pass `null` for menuModules to disable the filter (legacy entities).
 * @param {string|null|undefined} routeName
 * @param {string[]|Set<string>|null|undefined} menuModules
 * @returns {boolean}
 */
export function isMenuRouteAllowedByModules(routeName, menuModules) {
    if (!menuModules) {
        return true;
    }
    if (!routeName) {
        return true;
    }
    if (ALWAYS_ALLOWED_MENU_ROUTES.has(routeName)) {
        return true;
    }
    const allowed = menuModules instanceof Set ? menuModules : new Set(menuModules);
    if (allowed.size === 0) {
        return ALWAYS_ALLOWED_MENU_ROUTES.has(routeName);
    }
    if (allowed.has(routeName)) {
        return true;
    }
    const canonical = MENU_MODULE_ALIASES[routeName] || routeName;
    if (allowed.has(canonical)) {
        return true;
    }
    if (
        routeName === "comparator" ||
        routeName === "replenish" ||
        routeName === "toorder" ||
        routeName === "providerorders-toapprove"
    ) {
        return allowed.has("providers") || allowed.has("providerorders");
    }
    if (routeName === "salesinsights") {
        return allowed.has("commissions") || allowed.has("customerorders");
    }
    if (routeName === "providerinvoices") {
        return (
            allowed.has("providerinvoices") ||
            allowed.has("providerorders") ||
            allowed.has("providers")
        );
    }
    return false;
}

/**
 * Onboarding no longer filters the sidebar: every permitted menu item is on
 * from account creation. Users hide entries via Configure menu.
 * @returns {null}
 */
export function getOnboardingMenuModules() {
    return null;
}

function runRouteCheck(checks, routeName) {
    if (!routeName) {
        return true;
    }
    const check = checks[routeName];
    if (!check) {
        return true;
    }
    return check();
}

/** Route access (permissions). Used by router guards and home shortcuts. */
export function canAccessRoute(routeName) {
    return runRouteCheck(ROUTE_PERMISSION_CHECKS, routeName);
}

/**
 * Establishments list/detail: align direct URL access with menu scopes
 * (incl. owner bypass). Retailers always have the establishments feature.
 */
export function canAccessEstablishmentsRoute() {
    if (!entityHasFeature(store, "establishments")) {
        return false;
    }
    return (
        canAccessNavigationRoute("establishments") ||
        utils.userHasPermission("core.view_establishment")
    );
}

/** Table links and inline navigation (scopes). */
export function canAccessNavigationRoute(routeName) {
    return runRouteCheck(MENU_SCOPE_CHECKS, routeName);
}

/** Menu item visibility (scopes). Onboarding no longer filters the sidebar. */
export function canAccessNavigationItem(item) {
    if (!item) {
        return false;
    }
    if (item.url) {
        return true;
    }
    return canAccessNavigationRoute(item.to?.name);
}

export function canAccessNavigationGroup(group) {
    if (!group?.items?.length) {
        return true;
    }
    return group.items.some(
        (subitem) => !subitem.hidden && canAccessNavigationItem(subitem),
    );
}

export function installConnectedRoutePermissionGuard(router) {
    router.beforeEach((to, from, next) => {
        if (to.name && !canAccessRoute(to.name)) {
            next({ name: "notfound", replace: true });
            return;
        }
        next();
    });
}
